import { type IMediaService } from './IMediaService';
import { MediaApi } from 'infrastructure/apis/media/MediaApi';
import { CloudinaryApi } from 'infrastructure/apis/cloudinary/CloudinaryApi';
import { MediaDownloadRequestDTO, MediaDownloadResponseDTO } from 'infrastructure/dtos/media';
import { CloudinaryUploadRequestDTO, CloudinaryUploadResponseDTO } from 'infrastructure/dtos/media';

export class MediaService implements IMediaService {
  constructor(
    readonly api: MediaApi,
    readonly cloudinaryApi: CloudinaryApi
  ) {}

  async downloadMedia(dto: MediaDownloadRequestDTO): Promise<MediaDownloadResponseDTO> {
    return await this.api.downloadMedia(dto);
  }

  async cleanupTempFile(filePath: string): Promise<void> {
    await this.api.cleanupTempFile(filePath);
  }

  async uploadImageToCloudinary(
    dto: CloudinaryUploadRequestDTO
  ): Promise<CloudinaryUploadResponseDTO> {
    return await this.cloudinaryApi.uploadImage(dto);
  }

  async uploadVideoToCloudinary(
    dto: CloudinaryUploadRequestDTO
  ): Promise<CloudinaryUploadResponseDTO> {
    return await this.cloudinaryApi.uploadVideo(dto);
  }

  async downloadAndUploadToCloudinary(
    url: string,
    uploadPreset: 'instagram_pictures' | 'instagram_videos'
  ): Promise<CloudinaryUploadResponseDTO> {
    try {
      // Download the file
      const downloadResult = await this.downloadMedia({ url });

      // Convert blob URL to File object with proper MIME type
      const response = await fetch(downloadResult.filePath);
      const blob = await response.blob();

      // Determine the correct MIME type
      const mimeType = downloadResult.mimeType || this.getMimeTypeFromUrl(url);

      const file = new File([blob], downloadResult.filename, {
        type: mimeType,
      });

      // Upload to Cloudinary based on type
      let uploadResult: CloudinaryUploadResponseDTO;

      if (uploadPreset === 'instagram_pictures') {
        uploadResult = await this.uploadImageToCloudinary({
          file,
          uploadPreset: 'instagram_pictures',
        });
      } else {
        uploadResult = await this.uploadVideoToCloudinary({
          file,
          uploadPreset: 'instagram_videos',
        });
      }

      // Clean up the temporary blob URL
      await this.cleanupTempFile(downloadResult.filePath);

      return uploadResult;
    } catch (error) {
      throw new Error(`Failed to download and upload to Cloudinary: ${error}`);
    }
  }

  private getMimeTypeFromUrl(url: string): string {
    const extension = url.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg',
      avi: 'video/avi',
      mov: 'video/quicktime',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
    };

    return mimeTypes[extension || ''] || 'application/octet-stream';
  }
}
